import type { BollingerBands, DonchianChannels, KeltnerChannels } from '../../schemas/indicatortypes.ts';
import { MovingAverages } from './movingAverages';

// Volatility Indicators
export class VolatilityIndicators {
    /**
     * Calculate Bollinger Bands
     */
    static calculateBollingerBands(data: number[], period = 20, stdDev = 2): BollingerBands[] {
        if (data.length < period) return [];

        const sma = MovingAverages.calculateSMA(data, period);
        const bands: BollingerBands[] = [];

        for (let i = 0; i < sma.length; i++) {
            const dataSlice = data.slice(i, i + period);
            const mean = sma[i];
            const variance = dataSlice.reduce((sum: number, price: number) => sum + (price - mean) ** 2, 0) / period;
            const standardDeviation = Math.sqrt(variance);

            const upper = mean + standardDeviation * stdDev;
            const lower = mean - standardDeviation * stdDev;

            bands.push({
                upper,
                middle: mean,
                lower,
                bandwidth: ((upper - lower) / mean) * 100,
                percentB: ((data[i] - lower) / (upper - lower)) * 100,
            });
        }

        return bands;
    }

    /**
     * Calculate Average True Range (ATR)
     */
    static calculateATR(prices: number[], highs: number[], lows: number[]): number[] {
        if (prices.length < 2) return [];

        const trueRanges: number[] = [];
        for (let i = 1; i < prices.length; i++) {
            const high = highs[i] || prices[i];
            const low = lows[i] || prices[i];
            const prevClose = prices[i - 1];

            const tr1 = high - low;
            const tr2 = Math.abs(high - prevClose);
            const tr3 = Math.abs(low - prevClose);

            trueRanges.push(Math.max(tr1, tr2, tr3));
        }

        // Calculate 14-period ATR
        const atr: number[] = [];
        if (trueRanges.length >= 14) {
            let sum = trueRanges.slice(0, 14).reduce((a, b) => a + b, 0);
            atr.push(sum / 14);

            for (let i = 14; i < trueRanges.length; i++) {
                sum = sum - trueRanges[i - 14] + trueRanges[i];
                atr.push(sum / 14);
            }
        }

        return atr;
    }

    /**
     * Calculate Keltner Channels
     */
    static calculateKeltnerChannels(prices: number[], highs: number[], lows: number[]): KeltnerChannels[] {
        const keltner: KeltnerChannels[] = [];
        const period = 20;
        const multiplier = 2;

        if (prices.length < period) return [];

        // Calculate EMA of prices
        const ema = MovingAverages.calculateEMA(prices, period);

        // Calculate ATR
        const atr = VolatilityIndicators.calculateATR(prices, highs, lows);

        for (let i = 0; i < Math.min(ema.length, atr.length); i++) {
            const middle = ema[i];
            const atrValue = atr[i];

            keltner.push({
                upper: middle + multiplier * atrValue,
                middle: middle,
                lower: middle - multiplier * atrValue,
            });
        }

        return keltner;
    }

    /**
     * Calculate Donchian Channels
     */
    static calculateDonchianChannels(prices: number[]): DonchianChannels[] {
        const donchian: DonchianChannels[] = [];
        for (let i = 20; i < prices.length; i++) {
            const slice = prices.slice(i - 20, i);
            donchian.push({
                upper: Math.max(...slice),
                middle: (Math.max(...slice) + Math.min(...slice)) / 2,
                lower: Math.min(...slice),
            });
        }
        return donchian;
    }

    /**
     * Calculate Chaikin Volatility
     */
    static calculateChaikinVolatility(prices: number[], highs: number[], lows: number[]): number[] {
        const volatility: number[] = [];
        const period = 10;

        if (prices.length < period * 2) return [];

        // Calculate EMA of high-low range
        const highLowRange: number[] = [];
        for (let i = 0; i < prices.length; i++) {
            const high = highs[i] || prices[i];
            const low = lows[i] || prices[i];
            highLowRange.push(high - low);
        }

        const emaRange = MovingAverages.calculateEMA(highLowRange, period);

        // Calculate Chaikin Volatility
        for (let i = period; i < emaRange.length; i++) {
            const currentEMA = emaRange[i];
            const pastEMA = emaRange[i - period];

            const volatilityValue = pastEMA !== 0 ? ((currentEMA - pastEMA) / pastEMA) * 100 : 0;
            volatility.push(volatilityValue);
        }

        return volatility;
    }
}
